import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { makeStyles } from '@material-ui/core'
import { useRouter } from 'next/router'
import DomesticForm from '../checkprice/DomesticForm'
import InterForm from '../checkprice/InterForm'
import T, { specularRing, specularHost, specularRingReduced } from './tokens'

// Design-matched check-price form for the new homepage only.
// Reuses the shared DomesticForm / InterForm logic; adds a 3rd
// "ตรวจสอบสถานะ" (track parcel) tab. The shared CheckpriceForm used on
// the old homepage (/) is intentionally left untouched.

const TABS = [
  {
    key: 'domestic',
    labelKey: 'checkpriceForm.tabDomestic',
    icon: '/static/img/home/checkprice-icon-truck.svg',
  },
  {
    key: 'inter',
    labelKey: 'checkpriceForm.tabInter',
    icon: '/static/img/home/checkprice-icon-plane.svg',
  },
  {
    key: 'track',
    labelKey: 'checkpriceForm.tabTrack',
    icon: null, // inline SVG below
  },
]

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
  },
  tabRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '6px',
    // Flush with the card's left edge (was 8px) — the design's active tab
    // shares the exact same left edge as the card below it, so its own
    // square bottom-left corner masks the card's rounded top-left corner
    // and the two read as one seamless "folder tab" surface. The previous
    // 8px inset left an uncovered sliver of the card's rounded corner
    // exposed to its left, which is what showed up as the hard right-angle
    // notch the user circled.
    paddingLeft: 0,
    marginBottom: -2,
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('xs')]: {
      gap: '3px',
      paddingLeft: 0,
      justifyContent: 'space-between',
    },
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '17px 32px',
    border: 'none',
    borderRadius: '16px 16px 0 0',
    cursor: 'pointer',
    fontFamily: T.font.heading,
    fontSize: 19,
    fontWeight: 500,
    lineHeight: 1,
    background: 'rgba(233, 243, 255, 0.75)',
    color: T.color.muted,
    transition: 'background 0.25s ease, color 0.25s ease',
    '& img, & svg': {
      width: 30,
      height: 30,
      objectFit: 'contain',
      flexShrink: 0,
    },
    '&:hover': {
      background: 'rgba(233, 243, 255, 1)',
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      gap: '4px',
      padding: '10px 6px',
      fontSize: 13,
      flex: 1,
      textAlign: 'center',
      '& img, & svg': { width: 24, height: 24 },
    },
  },
  tabActive: {
    // Same frosted-glass fill as `card` (see tokens.js `glass`) so the
    // active tab and the card below read as one continuous panel, per the
    // design mockup — not a separate opaque surface sitting on top of a
    // translucent one. Dropped the old ambient box-shadow: with matching
    // fill it was an unnecessary seam-adjacent artifact once the surfaces
    // are translucent (any shadow ring around the tab box would show
    // through the glass at the tab/card join).
    background: T.glass.panel,
    backdropFilter: T.glass.blur,
    WebkitBackdropFilter: T.glass.blur,
    color: T.color.ink,
    boxShadow: 'none',
    '&:hover': {
      background: T.glass.panel,
    },
  },
  card: {
    // Frosted-glass panel (design mockup: the hero's world-map/globe art
    // shows faintly through the card) instead of solid white. Alpha kept
    // high (0.82) so inner text stays comfortably AA-contrast even over
    // the busiest part of the banner artwork behind it.
    background: T.glass.panel,
    backdropFilter: T.glass.blur,
    WebkitBackdropFilter: T.glass.blur,
    // Top-LEFT corner squared (0) so the active/left tab's square bottom sits
    // flush over it and tab+card read as one seamless "folder tab" left edge
    // (per the design mockup). Top-right + both bottom corners stay rounded.
    // A uniform 28 here reintroduces the notch at the tab/card join.
    borderRadius: '0 28px 28px 28px',
    boxShadow: T.shadow.lg,
    // Enlarged to match the design's more generous card — bigger padding
    // and taller spacing between the field rows (see .MuiGrid-item below).
    padding: '26px 40px 30px',
    position: 'relative',
    zIndex: 1,
    [theme.breakpoints.down('xs')]: {
      padding: '20px 18px 24px',
      // On mobile the tab row spans the full card width, so both top corners
      // sit under tabs → square both; keep the bottom corners rounded.
      borderRadius: '0 0 18px 18px',
    },
    // Taller gap between the two rows of fields (shared DomesticForm /
    // InterForm components render a MUI Grid with a small default spacing;
    // bumped here, scoped to this card only, without touching those files).
    '& .MuiGrid-item': {
      paddingBottom: '12px !important',
    },
    // Hide the inner heading rendered by DomesticForm / InterForm —
    // we render one centered heading below instead.
    '& h3': {
      display: 'none',
    },
    // Apply Sukhumvit Set to the shared form's text elements (they
    // otherwise inherit the global Kanit theme font). Targets text-bearing
    // nodes only, never icon fonts.
    '& input, & textarea, & select, & label, & button, & .MuiInputBase-root, & .MuiInputBase-input, & .MuiFormLabel-root, & .MuiButton-label, & .MuiTypography-root, & p, & a': {
      fontFamily: `${T.font.body} !important`,
    },
    // Match the design: rounder field corners and a fully-rounded (pill)
    // submit button with slightly larger label.
    '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    // Shared "specular ring" treatment (see tokens.js `specularRing` /
    // `specularHost`) — the same continuously-rotating blue conic-gradient
    // ring used on every primary CTA across /new (track button, header
    // "ลูกค้าองค์กร" pill, ShippopBusinessNew pill). The blue gradient fill
    // + white label come from the shared DomesticForm/InterForm button
    // itself (untouched) — this only adds the ring around it, via ::before,
    // so contrast stays intact. No glow.
    //
    // Implementation choice: the ring's rotation is driven by animating
    // the `--promo-angle` custom property that feeds
    // `conic-gradient(from var(--promo-angle), ...)` (registered via
    // @property in pages/new/index.js's global <style>, since JSS can't
    // emit @property). This is deliberately NOT a `transform: rotate()`
    // on the ::before element itself: this button is a wide pill (58px
    // tall, much wider), and rotating the whole masked ring as a rigid
    // body would visibly warp the capsule silhouette away from the
    // button's real (static) outline at every angle except 0/180deg.
    // Animating only the gradient's start angle keeps the mask/ring
    // geometry perfectly static and pill-shaped at all times — only the
    // hue sweeps. In browsers without @property support (pre-128
    // Firefox, pre-16.4 Safari), the custom property is simply inert:
    // `var(--promo-angle, 0deg)` falls back to a fixed 0deg, so the ring
    // degrades cleanly to a static gradient outline instead of animating
    // — never a warped or broken box.
    //
    // On top of the ring, a ONE-TIME entrance "glow sweep" plays on mount
    // via ::after — a bright diagonal highlight that sweeps across the
    // button once (non-infinite, `animation-fill-mode: both`), then
    // disappears, leaving only the continuous ring. reduced-motion skips
    // the sweep entirely (see the media query below).
    '& button[type="submit"]': {
      ...specularHost,
      borderRadius: '999px',
      minHeight: 50,
      fontSize: '16px',
      overflow: 'hidden',
      // Unify the fill with the header "ลูกค้าองค์กร" pill (corporateBtn in
      // HeaderNew.js) — both are solid blue-filled specular-ring CTAs and
      // should share the same gradient, not MUI's flat solid primary blue.
      // !important is required: MUI's .MuiButton-containedPrimary sets
      // background-color and otherwise wins the JSS cascade here regardless
      // of this rule's selector specificity. Hover/focus are pinned to the
      // same gradient (MUI's contained-button hover would otherwise swap in
      // a flat darker blue) with a brightness bump, matching corporateBtn's
      // hover feel instead of a background swap.
      background: `${T.gradient.blue} !important`,
      '&:hover, &:focus': {
        background: `${T.gradient.blue} !important`,
        filter: 'brightness(1.06)',
      },
      '&::before': specularRing(T.ring.blue),
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '40%',
        height: '100%',
        background:
          'linear-gradient(115deg, transparent 0%, rgba(255,255,255,0.75) 50%, transparent 100%)',
        transform: 'translateX(-160%) skewX(-20deg)',
        pointerEvents: 'none',
        animation: '$submitEntranceSweep 1.1s cubic-bezier(0.22, 1, 0.36, 1) 0.5s 1 both',
      },
      '@media (prefers-reduced-motion: reduce)': {
        ...specularRingReduced,
        '&::after': { animation: 'none', content: 'none' },
      },
    },
    // Design shows a brand-blue outline on every field (resting, hover and
    // focused), not MUI's default gray. Color sampled directly from the
    // design mockup's input border pixels (#008EFF). Scoped to this card
    // only — the shared DomesticForm/InterForm components are untouched.
    // Note: border-width needs !important — MUI's internal
    // PrivateNotchedOutline-root-* rule sets border-width:1px and, despite
    // this override having higher selector specificity, still wins the
    // cascade in this JSS setup (component-level style insertion order
    // trumps specificity here), so !important is required to force it.
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#008EFF',
      borderWidth: '1.5px !important',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0072D6',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#008EFF',
      borderWidth: '2px !important',
    },
    // "Star Border" (React Bits: reactbits.dev/animations/star-border)
    // reimplemented in pure CSS — the library version depends on
    // framer-motion, incompatible with this React 16 / webpack 4 stack.
    // Reuses the same rotating conic-gradient `--promo-angle` ring infra as
    // every CTA on /new (tokens.js `specularRing`, driven by the `promo-spin`
    // keyframes registered globally in pages/new/index.js), tinted with the
    // same `T.ring.blue` family (#009EFF/#33C9FF) already used elsewhere —
    // i.e. a close relative of this field's own resting #008EFF border, not
    // a different hue, per the design ask ("สีใกล้เคียงกับ border เดิม").
    // The ::after only exists (content) on hover/focus — resting fields are
    // pixel-identical to before. It's given a z-index above the outline
    // fieldset (which paints at the default z-index:auto stacking step) so
    // the travelling light draws over the flat border while active, on all
    // fields across every tab (scoped through this shared selector, not the
    // DomesticForm/InterForm components themselves).
    '& .MuiOutlinedInput-root': {
      position: 'relative',
      '&:hover::after, &.Mui-focused::after': {
        ...specularRing(T.ring.blue, '1.5px'),
        zIndex: 2,
      },
      '@media (prefers-reduced-motion: reduce)': {
        // Drop the ring pseudo-element entirely — falls back to the plain
        // (already reduced-motion-safe) hover/focus color changes above.
        '&:hover::after, &.Mui-focused::after': {
          content: 'none',
        },
      },
    },
    // Blue dropdown chevrons — native MuiSelect fields (e.g. box size) use
    // .MuiSelect-icon; the origin/destination Autocomplete fields render
    // their arrow inside .MuiAutocomplete-popupIndicator instead.
    '& .MuiSelect-icon': {
      color: '#008EFF',
    },
    // 48×48 hit area for the origin/destination dropdown arrow (Lighthouse
    // tap-targets). Fields are minHeight 50 so it fits without growing them;
    // the arrow icon stays centered and visually unchanged.
    // Align the origin/destination dropdown arrow with the box-size Select
    // icon. The enlarged 48×48 hit-area button broke MUI's centering
    // (its default endAdornment top:calc(50% - 14px) assumes a ~28px indicator),
    // leaving the arrow ~10px low and ~12px too far left ("สามเหลี่ยมไม่ตรง").
    // Center the endAdornment vertically, and nudge the button right so its
    // arrow lands on the same right column as the box-size arrow.
    '& .MuiAutocomplete-popupIndicator': {
      width: 48,
      height: 48,
      position: 'relative',
      right: -12,
    },
    // No circular IconButton hover tint on the arrow (the box-size Select icon
    // has none, so this keeps the two dropdowns consistent).
    '& .MuiAutocomplete-popupIndicator:hover': {
      backgroundColor: 'transparent',
    },
    '& .MuiAutocomplete-endAdornment': {
      top: '50%',
      transform: 'translateY(-50%)',
    },
    '& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root': {
      color: '#008EFF',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 27,
    fontWeight: 600,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 18px',
    // Blur-Text style entrance (React Bits vibe) — plays on mount + tab change
    // (keyed). Thai has no word spaces so we reveal the whole line, not glyphs.
    animation: '$headingIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) both',
    [theme.breakpoints.down('xs')]: {
      fontSize: 22,
      margin: '0 0 16px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  '@keyframes headingIn': {
    from: { opacity: 0, transform: 'translateY(12px)', filter: 'blur(6px)' },
    to: { opacity: 1, transform: 'none', filter: 'blur(0)' },
  },
  '@keyframes submitEntranceSweep': {
    '0%': { transform: 'translateX(-160%) skewX(-20deg)', opacity: 0 },
    '12%': { opacity: 1 },
    '100%': { transform: 'translateX(320%) skewX(-20deg)', opacity: 0 },
  },
  panel: {
    '&[hidden]': {
      display: 'none',
    },
  },
  // Track-parcel tab
  trackForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: 560,
    margin: '0 auto',
  },
  trackInput: {
    width: '100%',
    height: 52,
    padding: '0 18px',
    fontSize: 16,
    fontFamily: T.font.body,
    color: T.color.ink,
    background: '#fff',
    border: `1.5px solid ${T.color.line}`,
    borderRadius: 12,
    outline: 'none',
    transition: 'border-color 0.2s ease',
    '&:focus': {
      borderColor: T.color.brand,
    },
    '&::placeholder': {
      color: T.color.faint,
    },
  },
  // Track-parcel submit — part of the shared "specular ring" CTA family
  // (see tokens.js), same continuous blue ring as the check-price submit
  // button, tinted identically since it's the same solid blue-filled pill.
  trackBtn: {
    ...specularHost,
    height: 52,
    border: 'none',
    borderRadius: 999,
    background: T.gradient.blue,
    color: T.color.onBrand,
    fontFamily: T.font.heading,
    fontSize: 17,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
    '&::before': specularRing(T.ring.blue),
    '&:hover': {
      filter: 'brightness(1.05)',
      transform: 'translateY(-2px)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      ...specularRingReduced,
    },
  },
}))

const TrackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2.5 3.5 6.75v10.5L12 21.5l8.5-4.25V6.75L12 2.5Z"
      stroke="#FF7A1A"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M3.7 6.9 12 11l8.3-4.1M12 11v10.3"
      stroke="#FF7A1A"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
)

const CheckpriceFormNew = ({ required = false }) => {
  const classes = useStyles()
  const { t } = useTranslation('home')
  const Router = useRouter()
  const [activeTab, setActiveTab] = useState(0)
  const [trackNo, setTrackNo] = useState('')

  // NEW is the default design now, so results open in the new design via the
  // global Context `onRouting` — no local override needed.
  const submitTrack = (e) => {
    e.preventDefault()
    const code = trackNo.trim()
    if (!code) return
    Router.push(`/tracking?tracking_code=${encodeURIComponent(code)}`)
  }

  const headingText =
    activeTab === 2
      ? t('checkpriceForm.headingTrack')
      : t('checkpriceForm.headingPrice')

  return (
    <div className={classes.wrapper}>
      <div className={classes.tabRow} role="tablist">
        {TABS.map((tab, i) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === i}
            className={`${classes.tab} ${
              activeTab === i ? classes.tabActive : ''
            }`}
            onClick={() => setActiveTab(i)}
            type="button"
          >
            {tab.icon ? (
              <img
                src={tab.icon}
                alt=""
                aria-hidden="true"
                width={30}
                height={30}
                decoding="async"
              />
            ) : (
              <TrackIcon />
            )}
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div className={classes.card}>
        <h2 className={classes.heading} key={headingText}>
          {headingText}
        </h2>

        <div className={classes.panel} hidden={activeTab !== 0}>
          <DomesticForm submitMode="redirect" required={required} key="domesticForm" />
        </div>
        <div className={classes.panel} hidden={activeTab !== 1}>
          <InterForm submitMode="redirect" required={required} key="interForm" />
        </div>
        <div className={classes.panel} hidden={activeTab !== 2}>
          <form className={classes.trackForm} onSubmit={submitTrack}>
            <input
              className={classes.trackInput}
              type="text"
              placeholder={t('checkpriceForm.trackPlaceholder')}
              value={trackNo}
              onChange={(e) => setTrackNo(e.target.value)}
              aria-label={t('checkpriceForm.trackAria')}
            />
            <button className={classes.trackBtn} type="submit">
              {t('checkpriceForm.trackButton')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CheckpriceFormNew
